import { useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { formatErrorMessage } from "@utils/formatErrorMessage";
import { useUserProfile } from "@providers/profile";
import { GlobalError } from "@components/primitives/input";
import CreditCardForm from "./CreditCardForm";
import CreditCardInfo from "./CreditCardInfo";
import { initialCardState, CardState, CreditCardProps } from "./CreditCard.model";

const CreditCard = ({ card, showAddress, states, dispatchBilling }: CreditCardProps) => {
  const [cardState, setCardState] = useState<CardState>(initialCardState);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe>>();

  const {
    setupPaymentIntents,
    profileState: { authToken },
  } = useUserProfile();

  // Resolves "Unsupported prop change on stripe element" warning
  useEffect(() => {
    setStripePromise(loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY));
  }, []);

  const onEdit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Track cardState in separate object through function
    let editState = { ...cardState, error: "", editing: true };
    dispatchBilling((prevState) => ({
      ...prevState,
      loading: true,
    }));
    setCardState({ ...editState });

    try {
      const intents = await setupPaymentIntents(authToken);
      editState = { ...editState, secret: intents.client_secret };
    } catch (e) {
      editState = { ...editState, error: formatErrorMessage(e) };
    }

    setCardState({ ...editState });
    dispatchBilling((prevState) => ({
      ...prevState,
      loading: false,
    }));
  };

  const onCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setCardState({ ...initialCardState });
  };

  return (
    <section>
      {cardState.error && (
        <div className="cell small-12 global-error">
          <GlobalError>{cardState.error}</GlobalError>
        </div>
      )}
      <h5>Credit Card</h5>
      {cardState.editing ? (
        <Elements stripe={stripePromise}>
          <CreditCardForm
            card={card}
            cardStatus={cardState}
            dispatchCardStatus={setCardState}
            showAddress={showAddress}
            dispatchBilling={dispatchBilling}
            states={states}
            onCancel={onCancel}
          />
        </Elements>
      ) : (
        <CreditCardInfo card={card} onEdit={onEdit} />
      )}
    </section>
  );
};

export default CreditCard;
